import formatMessageForRender from '@/utils/message_render'
import formatReceiveMessageName from '@/utils/message_receive'
import throttle from 'lodash.throttle'
import { getMessageHistory } from '@/api/chat'
import { HOST, STATUS, MESSAGE_SENDER } from '@/constants/message'
import { scrollTo } from '@/utils/dom'
import { isObject } from '@/utils'

const FIXED_PAGE_NUM = 1 // 历史记录主要基于 endTime 判断加载 DEFAULT_PAGE_SIZE 个内容，故页码固定为 1
const DEFAULT_PAGE_SIZE = 20 // 历史记录每次加载多少个
const THROTTLE_TIME = 300 // scroll 事件的节流时间
const GET_HISTORY_DISTANCE = 0 // 消息列表滚到距离顶部多少距离时才加载历史记录

export default {
    data () {
        return {
            isHistoryInited: false,
            isLoading: false,
            isLoadedAll: false,
            scrollThrottled: throttle(this.detectFetchHistory, THROTTLE_TIME)
        }
    },
    watch: {
        isLoadedAll (value) {
            if (value) {
                this.unbindHistoryEvents()
            }
        },
        isHistoryInited (value) {
            if (value) {
                this.bindHistoryEvents()
            }
        }
    },
    methods: {
        async resetHistory () {
            this.isHistoryInited = false
            this.isLoading = false
            this.isLoadedAll = false
            this.unbindHistoryEvents()
            this.bindHistoryEvents()
            await this.initHistory()
        },
        async initHistory () {
            await this.fetchMessageHistory()
            const $messageList = this.$refs.messageList.$el
            this.$nextTick(() => {
                setTimeout(() => {
                    scrollTo($messageList, 0, 'scrollHeight')
                }, 200)
            })
        },
        detectFetchHistory () {
            const $messageList = this.$refs.messageList.$el
            const scrollTop = $messageList.scrollTop
            if (scrollTop <= GET_HISTORY_DISTANCE) {
                this.reachScrollTop()
            }
        },
        reachScrollTop () {
            if (this.isLoading || this.isLoadedAll) {
                return
            }
            this.fetchMessageHistory()
        },
        getEarliestMessageServerTimestamp () {
            if (this.getMessageLength() === 0) {
                return
            }
            for (let i = 0, len = this.getMessageLength(); i < len; i++) {
                const timestampFromServer = this.getMessageValueByIndex(i, 'server.timestamp')
                if (timestampFromServer) {
                    return timestampFromServer
                }
            }
            return
        },
        /**
         * 历史消息加载完毕 滚动到 加载前的第一条消息
         */
        scrollToBeforeHistory () {
            const $messageList = this.$refs.messageList.$el
            const { scrollHeight, scrollTop } = $messageList
            this.$nextTick(() => {
                const currentScrollHeight = $messageList.scrollHeight
                $messageList.scrollTop = currentScrollHeight - scrollHeight + scrollTop
            })
        },
        async fetchMessageHistory () {
            if (this.isLoading || this.isLoadedAll) {
                return
            }
            this.isLoading = true

            try {
                /**
                 * 消息列表按 created_at 逆序，即第 0 个离现在最近
                 */
                const endTime = this.getEarliestMessageServerTimestamp()
                const { list, pagination } = await getMessageHistory({
                    uid: this.uid,
                    endTime,
                    pageNum: FIXED_PAGE_NUM,
                    pageSize: DEFAULT_PAGE_SIZE + (endTime ? 1 : 0)
                })
                const messageListSegment = this.generateMessageListSegment(
                    this.filterExistMessage(list)
                )
                if (messageListSegment.length > 0) {
                    this.insertMessageSegmentByIndex(0, messageListSegment)
                    this.scrollToBeforeHistory()
                }
                // total_count 是指当前 page（1）和 endTime 得到的数量
                if (DEFAULT_PAGE_SIZE > pagination.total_count) {
                    this.isLoadedAll = true
                }
                if (!this.isHistoryInited) {
                    this.isHistoryInited = true
                }
            } catch (err) {
                // eslint-disable-next-line
                console.error(err)
            }  finally {
                this.isLoading = false
            }
        },
        filterExistMessage (messageList) {
            const allExitIdsFromServer = getAllExitIdsFromServer.call(this)

            return messageList.filter(message => {
                return !allExitIdsFromServer.includes(message['msg_id'])
            })

            function getAllExitIdsFromServer () {
                return this.messageList
                    .filter(message => message.server && message.server.id)
                    .map(message => message.server.id)
            }
        },
        generateMessageListSegment (messageList) {
            const result = []
            let lastId
            for (let i = messageList.length - 1; i >= 0; i--) {
                const historyItem = messageList[i]
                const message = historyItem.msg

                if (message.detail_type === 'nested') {
                    for (let i = 0; i < message.data.length; i++) {
                        handleItem.call(this, historyItem, message.data[i])
                    }
                } else {
                    handleItem.call(this, historyItem, historyItem.msg)
                }
            }

            return result

            function handleItem (historyItem, message) {
                const {
                    msg_id: idFromServer,
                    created_at: timestampFromServer,
                    sender,
                    is_read: isRead
                } = historyItem

                const name = formatReceiveMessageName(
                    message.detail_type,
                    message.data
                )

                if (!name) {
                    // eslint-disable-next-line
                    console.error('未知消息类型', historyItem)
                    return
                }

                const robot = {}

                function detectHasClickAllNot (historyItem) {
                    let result

                    if (!isObject(historyItem.raw)) {
                        return result
                    }

                    const {
                        is_all_not: hasClickAllNot
                    } = historyItem.raw

                    if (typeof hasClickAllNot !== 'undefined') {
                        result = !!hasClickAllNot
                    }

                    return result
                }

                const hasClickAllNot = detectHasClickAllNot(historyItem)

                if (typeof hasClickAllNot !== 'undefined') {
                    robot.hasClickAllNot = hasClickAllNot
                }
                const messageForRender = formatMessageForRender(Object.assign({
                    name,
                    host: getHost(sender),
                    timestamp: timestampFromServer,
                    status: STATUS.SUCCESS,
                    content: message.show_data || message.data,
                    server: {
                        id: idFromServer,
                        timestamp: timestampFromServer,
                        fromId: historyItem.user_id,
                        from: MESSAGE_SENDER[historyItem.sender]
                    },
                    isUnread: isRead
                }, Object.keys(robot).length > 0 ? { robot } : {}))
                const needInsertTime = this.detectInsertTime(
                    timestampFromServer,
                    lastId,
                    'server',
                    result
                )

                if (needInsertTime) {
                    result.push(this.generateMessageTime(timestampFromServer))
                }
                result.push(messageForRender)
                lastId = messageForRender.baseProps.id
            }

            /**
             * @param { Number } sender - 发送者，1为访客，2为机器人，3为客服，4为系统
             * @returns { String }
             */
            function getHost (sender) {
                return sender === 1 ? HOST.USER : HOST.SERVICE
            }
        },
        bindHistoryEvents () {
            const $messageList = this.$refs.messageList.$el
            $messageList.addEventListener('scroll', this.scrollThrottled, {
                passive: true
            })
        },
        unbindHistoryEvents () {
            const $messageList = this.$refs.messageList.$el
            $messageList.removeEventListener('scroll', this.scrollThrottled, {
                passive: true
            })
        }
    },
    beforeDestroy () {
        this.unbindHistoryEvents()
    }
}
